// Pure helpers for pagination math. No React, no DOM — easy to unit-test.

const MAX_VISIBLE_PAGES: number = 5;

/**
 * Returns true if a navigation request to `targetPage` is allowed.
 * Disallowed when the wrapper is disabled/loading, the target equals the
 * current page (no-op), or the target falls outside [1, totalPages].
 */
export const canChangePage = (
  targetPage: number,
  currentPage: number,
  totalPages: number,
  disabled: boolean,
  isLoading: boolean
): boolean => {
  if (disabled || isLoading) {
    return false;
  }

  if (targetPage === currentPage) {
    return false;
  }

  if (targetPage < 1 || targetPage > totalPages) {
    return false;
  }

  return true;
};

/**
 * Returns the sliding window of page numbers to render in the middle of the
 * pagination bar. Caps at MAX_VISIBLE_PAGES; centers the current page when
 * possible; clamps to the start/end of the range near the edges.
 */
export const getVisiblePages = (
  totalPages: number,
  currentPage: number
): number[] => {
  if (totalPages <= 0) {
    return [];
  }

  if (totalPages <= MAX_VISIBLE_PAGES) {
    return rangeInclusive(1, totalPages);
  }

  const halfWindow: number = Math.floor(MAX_VISIBLE_PAGES / 2);

  let start: number = Math.max(1, currentPage - halfWindow);
  let end: number = Math.min(totalPages, start + MAX_VISIBLE_PAGES - 1);

  if (end - start + 1 < MAX_VISIBLE_PAGES) {
    start = Math.max(1, end - MAX_VISIBLE_PAGES + 1);
  }

  return rangeInclusive(start, end);
};

const rangeInclusive = (start: number, end: number): number[] => {
  const length: number = end - start + 1;
  return Array.from({ length }, (_, index: number) => start + index);
};
